import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import { type ExamName } from '../examTasks/examTaskTypes';

const EXAM_NAME_STORAGE_KEY = 'pteultra-admin:exam-name';

type ExamNameContextValue = {
    examName: ExamName;
    setExamName: (value: ExamName) => void;
};

const ExamNameContext = createContext<ExamNameContextValue | undefined>(undefined);

function readStoredExamName(): ExamName {
    if (typeof window === 'undefined') return 'Academic';

    const value = window.localStorage.getItem(EXAM_NAME_STORAGE_KEY);
    return value === 'Core' ? 'Core' : 'Academic';
}

type ExamNameProviderProps = {
    children: ReactNode;
};

export function ExamNameProvider({ children }: ExamNameProviderProps) {
    const [examName, setExamName] = useState<ExamName>(() => readStoredExamName());

    useEffect(() => {
        window.localStorage.setItem(EXAM_NAME_STORAGE_KEY, examName);
    }, [examName]);

    const value = useMemo(() => ({ examName, setExamName }), [examName]);

    return <ExamNameContext.Provider value={value}>{children}</ExamNameContext.Provider>;
}

export function useExamName() {
    const context = useContext(ExamNameContext);
    if (!context) {
        throw new Error('useExamName must be used within an ExamNameProvider');
    }

    return context;
}